import React from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Typography,
} from "@mui/material";

import { Organisation, User } from "../../../types";
import AdminTeamsTableRow from "./admin-teams-table-row";

type AdminTeamsTableProps = {
  teamsLoading: boolean;
  usersLoading: boolean;
  clientsLoading: boolean;
  filteredTeams: Organisation[];
  rowsPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  sortBy: string;
  sortOrder: "asc" | "desc";
  onSort: (field: string) => void;
  searchTerm?: string;
  userDictionary: Record<string, User>;
};

const AdminTeamsTable: React.FC<AdminTeamsTableProps> = ({
  teamsLoading,
  usersLoading,
  clientsLoading,
  filteredTeams,
  rowsPerPage,
  currentPage,
  onPageChange,
  sortBy,
  sortOrder,
  onSort,
  searchTerm,
  userDictionary,
}) => {
  const paginatedTeams = filteredTeams.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage,
  );

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ py: 0.5, whiteSpace: "nowrap", textAlign: "left" }}>
              <TableSortLabel
                active={sortBy === "name"}
                direction={sortBy === "name" ? sortOrder : "asc"}
                onClick={() => onSort("name")}
                sx={{ fontSize: "0.875rem" }}
              >
                Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ py: 0.5, whiteSpace: "nowrap", textAlign: "left" }}>
              <TableSortLabel
                active={sortBy === "status"}
                direction={sortBy === "status" ? sortOrder : "asc"}
                onClick={() => onSort("status")}
                sx={{ fontSize: "0.875rem" }}
              >
                Status
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ py: 0.5, textAlign: "left" }}>
              <TableSortLabel
                active={sortBy === "adminCount"}
                direction={sortBy === "adminCount" ? sortOrder : "asc"}
                onClick={() => onSort("adminCount")}
                sx={{ fontSize: "0.875rem" }}
              >
                Admins
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ py: 0.5, textAlign: "left", width: "100%" }}>
              <TableSortLabel
                active={sortBy === "userCount"}
                direction={sortBy === "userCount" ? sortOrder : "asc"}
                onClick={() => onSort("userCount")}
                sx={{ fontSize: "0.875rem" }}
              >
                Members
              </TableSortLabel>
            </TableCell>
            <TableCell
              align="right"
              sx={{ py: 0.5, fontSize: "0.875rem", whiteSpace: "nowrap" }}
            >
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {teamsLoading || usersLoading || clientsLoading ? (
            <TableRow>
              <TableCell colSpan={5} align="center">
                <Typography>Loading...</Typography>
              </TableCell>
            </TableRow>
          ) : filteredTeams.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center">
                <Typography color="text.secondary">
                  {searchTerm
                    ? "No teams match your search"
                    : "No teams found for the selected client"}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            paginatedTeams.map((team) => (
              <AdminTeamsTableRow
                key={team.organisationId}
                team={team}
                userDictionary={userDictionary}
              />
            ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={filteredTeams.length}
        page={currentPage}
        onPageChange={(_, nextPage) => onPageChange(nextPage)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[rowsPerPage]}
      />
    </TableContainer>
  );
};

export default AdminTeamsTable;
